import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';
import moment from 'moment';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { ProfilePopup } from '../../components/Popup/ProfilePopup';
import {
  clearSelectedUser,
  changeMode,
  loadUserDetails,
  loadDashboardUserTitles,
  updateSelectedUser,
  updateOrInsertUser,
} from '../../actions/userManage';
import { usePopupContext } from '../../contexts/PopupContext';

const UNKNOWN_ERROR = 'Unknown error';

/**
 * Editable fields of the profile form, API errors of other fields (including read-only ones) are shown as common ones.
 */
const FORM_FIELDS = [
  'userName',
  'firstName',
  'lastName',
  'email',
  'password',
  'rePassword',
  'dialpadUserId',
  'firefliesApiToken',
];

/**
 * Split API validation errors into errors of the form fields and the common ones.
 *
 * @param {object} responseBody Error response body.
 * @returns {{ fieldErrors: Immutable.Map, commonErrors: Immutable.List }} Errors.
 */
const getApiErrors = responseBody => {
  const errors = responseBody?.errors;

  if (!errors) {
    return { fieldErrors: Map(), commonErrors: List([responseBody?.message || UNKNOWN_ERROR]) };
  }

  return Object.entries(errors).reduce(
    ({ fieldErrors, commonErrors }, [field, messages]) => {
      const message = [].concat(messages).join(' ');

      if (FORM_FIELDS.includes(field)) {
        return { fieldErrors: fieldErrors.set(field, message), commonErrors };
      }

      return { fieldErrors, commonErrors: commonErrors.push(message) };
    },
    { fieldErrors: Map(), commonErrors: List() },
  );
};

/**
 * Format a date for the API.
 *
 * @param {Moment|string} date Date.
 * @returns {string|null} Date in the API format.
 */
const formatDate = date => (date ? moment(date).format('YYYY-MM-DD') : null);

/**
 * Popup to edit the current user's profile.
 *
 * @param {object} props Props.
 * @param {Immutable.Map} props.popup Popup data, its props: `userId` (current user) and `onSave` (called after saving).
 */
const ProfilePopupContainer = props => {
  const {
    popup,
    selectedUser,
    formError,
    saveStatus,
    clearSelectedUser,
    changeMode,
    loadUserDetails,
    loadDashboardUserTitles,
    updateSelectedUser,
    updateOrInsertUser,
  } = props;
  const { closePopup } = usePopupContext();
  const userId = popup.getIn(['props', 'userId']);
  const onSave = popup.getIn(['props', 'onSave']);
  const [apiErrors, setApiErrors] = useState(() => ({ fieldErrors: Map(), commonErrors: List() }));
  // Client validation errors are shown only after the first Save click.
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    clearSelectedUser();
    changeMode('edit');
    loadUserDetails(userId);
    loadDashboardUserTitles();
  }, [userId, clearSelectedUser, changeMode, loadUserDetails, loadDashboardUserTitles]);

  const clearApiError = useCallback(name => {
    setApiErrors(prev => ({ fieldErrors: prev.fieldErrors.delete(name), commonErrors: prev.commonErrors }));
  }, []);

  const handleFormChange = useCallback(
    event => {
      const { name, value } = event.target;

      clearApiError(name);

      if (name === 'shareFirefliesMeetings') {
        updateSelectedUser(name, value === 'true');
      } else {
        updateSelectedUser(name, value);
      }
    },
    [clearApiError, updateSelectedUser],
  );

  const handleSave = useCallback(() => {
    setIsSubmitted(true);

    if (saveStatus === 'error') return;

    const postingUser = selectedUser.toJS();

    if (!postingUser.supervisorId || postingUser.supervisorId < 0) {
      postingUser.supervisorId = null;
    }

    if (!postingUser.moduleId || postingUser.moduleId < 0) {
      delete postingUser.moduleId;
    }

    // Empty passwords mean the password is not changed.
    if (!postingUser.password && !postingUser.rePassword) {
      delete postingUser.password;
      delete postingUser.rePassword;
    }

    updateOrInsertUser(
      {
        ...postingUser,
        hireDate: formatDate(postingUser.hireDate),
        startDate: formatDate(postingUser.startDate),
        endDate: formatDate(postingUser.endDate),
        shareFirefliesMeetings: Boolean(postingUser.shareFirefliesMeetings),
      },
      {
        afterSuccess: () => {
          closePopup();
          onSave();
        },
        afterError: ({ responseBody }) => {
          setApiErrors(getApiErrors(responseBody));
        },
      },
    );
  }, [saveStatus, selectedUser, updateOrInsertUser, closePopup, onSave]);

  const errors = useMemo(() => (isSubmitted ? formError : Map()).merge(apiErrors.fieldErrors), [
    isSubmitted,
    formError,
    apiErrors.fieldErrors,
  ]);
  const isUserLoading = selectedUser.get('id') !== userId;
  const isSaving = saveStatus === 'saving';

  return (
    <ProfilePopup
      {...props}
      commonErrors={apiErrors.commonErrors}
      formError={errors}
      isSaveDisabled={isUserLoading || isSaving}
      loading={isUserLoading || isSaving}
      onCancel={closePopup}
      onFormChange={handleFormChange}
      onSave={handleSave}
    />
  );
};

ProfilePopupContainer.propTypes = {
  changeMode: PropTypes.func.isRequired,
  clearSelectedUser: PropTypes.func.isRequired,
  formError: PropTypes.instanceOf(Map).isRequired,
  loadDashboardUserTitles: PropTypes.func.isRequired,
  loadUserDetails: PropTypes.func.isRequired,
  popup: PropTypes.instanceOf(Map).isRequired,
  saveStatus: PropTypes.string.isRequired,
  selectedUser: PropTypes.instanceOf(Map).isRequired,
  updateOrInsertUser: PropTypes.func.isRequired,
  updateSelectedUser: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  dashboardUserTitles: state.user.get('dashboardUserTitles'),
  formError: state.user.get('formError'),
  selectedUser: state.user.get('selectedUser'),
  saveStatus: state.user.get('saveStatus'),
});

const mapDispatchToProps = {
  clearSelectedUser,
  changeMode,
  loadUserDetails,
  loadDashboardUserTitles,
  updateSelectedUser,
  updateOrInsertUser,
};

export { ProfilePopupContainer };
export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(ProfilePopupContainer));
